import { PrismaEnums } from '@airreading/schema/browser';
import { UserRoleStatus } from './data/index.js';

/**
 * Used for role checking only. ANY is not a role stored in the database
 */
const ANY = '*' as const;

export const ImpersonatingAsHeader = 'x-impersonating-as';

/**
 * AuthRole is a stripped down version of UserRole.
 * It only contains the role and schoolId which is enough to identify perform role/auth checks.
 */
export type AuthRole = {
  role: PrismaEnums.Role;
  schoolId: string | null;
  status: PrismaEnums.UserRoleStatus;
};

/**
 * Used for role checking only. ANY is not a role stored in the database
 */
export type AuthRoleWithAny = {
  role: PrismaEnums.Role | typeof ANY;
  schoolId: string | null | typeof ANY;
  status: PrismaEnums.UserRoleStatus;
};

export type WithAuthRole =
  | {
      user_roles: AuthRole[];
    }
  | {
      userRoles: AuthRole[];
    };

function userRolesOf(object: WithAuthRole): AuthRole[] {
  return 'user_roles' in object ? object.user_roles : object.userRoles;
}

export const isCoach = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'coach' && status === UserRoleStatus.active);
export const isCoachAllStatuses = (x: WithAuthRole) => userRolesOf(x).some(({ role }) => role === 'coach');
export const isCoachRole = ({ role }: AuthRole) => role === 'coach';
export const isStudent = (x: WithAuthRole) => userRolesOf(x).some(({ role }) => role === 'student');
export const isStudentRole = ({ role }: AuthRole) => role === 'student';
export const isAirAdmin = (x: WithAuthRole) => userRolesOf(x).some(({ role }) => role === 'air_admin');
export const isAirAdminRole = ({ role }: AuthRole) => role === 'air_admin';
export const isSchoolAdmin = (x: WithAuthRole) => userRolesOf(x).some(({ role }) => role === 'school_admin');
export const isSchoolAdminRole = ({ role }: AuthRole) => role === 'school_admin';
export const isSchoolContact = (x: WithAuthRole) => userRolesOf(x).some(({ role }) => role === 'school_contact');
export const isSchoolContactRole = ({ role }: AuthRole) => role === 'school_contact';

export const isEdgeCoach = (x: WithAuthRole) =>
  userRolesOf(x).some(({ role, status }) => role === 'edge_coach' && status === UserRoleStatus.active);
export const isEdgeCoachRole = ({ role }: AuthRole) => role === 'edge_coach';

export const isStudentOfSchool = (x: WithAuthRole, schoolIdToCheck: string) => {
  return userRolesOf(x).some(({ role, schoolId }) => role === 'student' && schoolId === schoolIdToCheck);
};
export const getSchoolIdOfStudent = (x: WithAuthRole): string | null | undefined => {
  return userRolesOf(x).find(isStudentRole)?.schoolId;
};

export const airAdmin = (): AuthRole => ({ role: 'air_admin', schoolId: null, status: 'active' });
export const coach = (): AuthRole => ({ role: 'coach', schoolId: null, status: 'active' });
export const student = (): AuthRole => ({ role: 'student', schoolId: ANY, status: 'active' });
export const studentOfSchool = (schoolId: string): AuthRole => ({ role: 'student', schoolId, status: 'active' });
export const schoolAdminOfSchool = (schoolId: string): AuthRole => ({
  role: 'school_admin',
  schoolId,
  status: 'active',
});
export const schoolContactOfSchool = (schoolId: string): AuthRole => ({
  role: 'school_contact',
  schoolId,
  status: 'active',
});

export const equalRoles = (role1: AuthRole | AuthRoleWithAny, role2: AuthRole | AuthRoleWithAny) => {
  const roleMatch = role1.role === ANY || role2.role === ANY || role1.role === role2.role;
  const schoolMatch = role1.schoolId === ANY || role2.schoolId === ANY || role1.schoolId === role2.schoolId;
  const statusMatch = role1.status === role2.status;

  return roleMatch && schoolMatch && statusMatch;
};
export const roleHash = (role: AuthRole) => `${role.role}:${role.schoolId}:${role.status}`;
export const deactivate = (role: AuthRole): AuthRole => ({ ...role, status: 'inactive' });
export const activateRole = (role: AuthRole): AuthRole => ({ ...role, status: 'active' });

export type SpecialRole = 'air_admin' | 'coach' | 'edge_coach';
export const SPECIAL_ROLES: readonly PrismaEnums.Role[] = Object.freeze(['air_admin', 'coach', 'edge_coach']);

/**
 * Determines if the given role is a special role, i.e. it has no `schoolId` associated with it.
 */
export function isSpecialRole(role: PrismaEnums.Role): role is SpecialRole {
  return SPECIAL_ROLES.includes(role);
}

export function partitionRoles(roles?: PrismaEnums.Role[]): [SpecialRole[], PrismaEnums.Role[]] {
  if (!roles) {
    return [[], []];
  }

  const specialRoles: SpecialRole[] = [];
  const regularRoles: PrismaEnums.Role[] = [];
  for (const role of roles) {
    if (isSpecialRole(role)) {
      specialRoles.push(role);
    } else {
      regularRoles.push(role);
    }
  }

  return [specialRoles, regularRoles];
}

/**
 * Returns fullNameOrEmail with " (Edge tutors)" appended only when the user is not a coach or student.
 * Call only when the user is an Edge coach: isEdgeCoach ? coachFullNameWithEdgeTutors(name, user) : name
 */
export function coachFullNameWithEdgeTutors(fullNameOrEmail: string, user: WithAuthRole | null | undefined): string {
  return `${fullNameOrEmail}${user && isAirAdmin(user) ? ' (Edge tutors)' : ''}`;
}
